/* eslint-disable jsx-a11y/heading-has-content */
import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BrowserRouter, Routes, Route, Link, useNavigate, } from "react-router-dom";
import { Menu, LogOut, X, Plus, Loader2, Pencil } from "lucide-react";
import { Toaster, toast } from "sonner";
import { googleLogout, useGoogleLogin } from '@react-oauth/google';
import { Button } from "@/components/ui/button";


import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

import PreviewTable from "./components/PreviewTable";
import Phase1 from "./components/Phase1";
import Phase2 from "./components/Phase2";
import Phase3 from "./components/Phase3";
import Phase4 from "./components/Phase4";
import Phase5 from "./components/Phase5";
import Phase6 from "./components/Phase6";
import AuditStepper from "./components/AuditStepper";
import AnalyticsDashboard from "./pages/Analytics";
import OutflowManager from "./components/OutflowManager";
import ErrorBoundary from "@/components/ErrorBoundary";

// Add Axios interceptor to attach Google Token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("google_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Automatically handle unauthorized and server errors globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const status = error.response.status;
      const detail = error.response.data?.detail || error.message;
      
      if (status === 401 || status === 403) {
        // Silently log out on session expiry to avoid scary red errors
        localStorage.removeItem("google_token");
        localStorage.removeItem("user_email");
        window.location.reload();
      } else {
        // Surface 500 internal server errors and other issues directly to the UI
        toast.error(`Server Error (${status}): ${detail}`);
      }
    } else {
      toast.error(`Network Error: ${error.message}. Is the backend running?`);
    }
    return Promise.reject(error);
  }
);

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
export const API = `${BACKEND_URL}/api`;

const PHASE_LABELS = {
  phase1: "Phase 1 — Master Sanitation",
  phase2: "Phase 2 — Master vs Tax",
  phase3: "Phase 3 — OTA Reconciliation",
  phase4: "Phase 4 — Razorpay & Gateway Audit",
  phase5: "Phase 5 — Final Revenue Summary",
};

export function MappingEditor({ session, role, onSaved }) {
  const detected = session?.column_mapping?.[role] || {};
  const [mapping, setMapping] = useState(detected);

  useEffect(() => {
    setMapping(session?.column_mapping?.[role] || {});
  }, [session, role]);

  const save = async () => {
    try {
      await axios.post(`${API}/audit/session/${session.id}/columns`, {
        role,
        mapping,
      });
      toast.success(`Mapping saved for ${role}`);
      onSaved?.();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    }
  };

  if (Object.keys(detected).length === 0)
    return (
      <p className="text-sm text-muted-foreground">
        Upload the {role} file first to detect columns.
      </p>
    );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.keys(detected).map((k) => (
          <div key={k}>
            <Label className="text-xs">{k}</Label>
            <Input
              data-testid={`mapping-${role}-${k}`}
              value={mapping[k] ?? ""}
              onChange={(e) =>
                setMapping({ ...mapping, [k]: e.target.value || null })
              }
              placeholder="(none)"
            />
          </div>
        ))}
      </div>
      <Button data-testid={`save-mapping-${role}-btn`} onClick={save} size="sm">
        Save mapping
      </Button>
    </div>
  );
}

export function FileUpload({ session, role, label, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `${API}/audit/session/${session.id}/upload?role=${encodeURIComponent(role)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setLast(res.data);
      toast.success(`${label}: ${res.data.rows} rows loaded`);
      onUploaded?.(res.data);
    } catch (err) {
      toast.error(`Upload failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${label}? Phase results depending on this will be cleared.`)) return;
    try {
      await axios.delete(
        `${API}/audit/session/${session.id}/file?role=${encodeURIComponent(role)}`
      );
      setLast(null);
      toast.success(`${label} removed`);
      onUploaded?.();
    } catch (err) {
      toast.error(`Delete failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const uploaded = !!session.files?.[role];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{label}</CardTitle>
          {uploaded ? (
            <Badge variant="secondary" data-testid={`badge-uploaded-${role}`}>
              Uploaded
            </Badge>
          ) : (
            <Badge variant="outline">Pending</Badge>
          )}
        </div>
        <CardDescription className="text-xs break-all">
          role: {role}
          {uploaded ? ` · ${session.files[role]}` : ""}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input
          data-testid={`upload-${role}-input`}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={upload}
          disabled={busy}
        />
        {uploaded && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              title="Download original file"
              onClick={async () => {
                try {
                  const token = localStorage.getItem("google_token");
                  const headers = {};
                  if (token) headers.Authorization = `Bearer ${token}`;
                  const res = await fetch(`${API}/audit/session/${session.id}/file/${role}`, {
                    headers
                  });
                  
                  if (res.status === 401 || res.status === 403) {
                    localStorage.removeItem("google_token");
                    localStorage.removeItem("user_email");
                    window.location.reload();
                    return;
                  }
                  
                  if (!res.ok) throw new Error("Failed to download file");
                  const blob = await res.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = session.files[role];
                  a.click();
                  window.URL.revokeObjectURL(url);
                } catch (e) {
                  toast.error(e.message);
                }
              }}
              disabled={busy}
            >
              Download
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid={`delete-${role}-btn`}
              onClick={remove}
              disabled={busy}
            >
              Remove
            </Button>
          </div>
        )}
        {last && (
          <div className="text-xs space-y-1">
            <div>Rows: {last.rows}</div>
            <details className="text-[11px]">
              <summary className="cursor-pointer text-muted-foreground">
                Detected mapping
              </summary>
              <pre className="bg-muted p-2 rounded overflow-x-auto mt-1">
                {JSON.stringify(last.detected_mapping, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Reusable per-phase "Approve & Lock" control.
 *
 * Shows different UI based on phase state:
 *   - phase hasn't completed yet           → nothing (no lock possible)
 *   - phase complete & unlocked            → green "✓ Approve & Lock" button
 *   - phase complete & locked              → amber banner + "🔓 Unlock to re-run"
 *
 * `phaseKey` matches the backend convention: "phase1", "phase2", "phase4",
 * or "phase3:GoMMT" / "phase3:Agoda" for OTA-scoped runs.
 *
 * Locking calls POST /lock-phase. Unlocking calls /unlock-phase (which also
 * resets session.verified on the backend since the auditor is re-opening a
 * phase).
 */
export function PhaseLockControl({ session, phaseKey, complete, refresh }) {
  const [busy, setBusy] = useState(false);
  const locked = (session.locked_phases || {})[phaseKey];

  const lock = async () => {
    const note = window.prompt(
      `Approve & lock ${phaseKey}?\n\nOptional reviewer note (e.g. "Reconciled with GoMMT POC 12-Mar"):`,
      ""
    );
    if (note === null) return; // user cancelled the prompt
    setBusy(true);
    try {
      await axios.post(`${API}/audit/session/${session.id}/lock-phase`, {
        phase: phaseKey,
        note: note || "",
      });
      toast.success(`${phaseKey} approved & locked`);
      refresh();
    } catch (e) {
      toast.error(`Lock failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const unlock = async () => {
    if (
      !window.confirm(
        `Unlock ${phaseKey}?\n\nThis will also reset 'Mark as verified' since you are re-opening a phase.`
      )
    )
      return;
    setBusy(true);
    try {
      await axios.post(`${API}/audit/session/${session.id}/unlock-phase`, {
        phase: phaseKey,
      });
      toast.success(`${phaseKey} unlocked — verified flag reset`);
      refresh();
    } catch (e) {
      toast.error(`Unlock failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (!complete) return null;

  if (locked) {
    const when = locked.locked_at ? new Date(locked.locked_at).toLocaleString() : "—";
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 flex flex-wrap items-center gap-3 text-sm">
        <span className="text-amber-800">
          🔒 <strong>Approved & locked</strong> at {when}
          {locked.note ? <span className="text-amber-700"> — "{locked.note}"</span> : null}
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={unlock}
          disabled={busy}
          data-testid={`unlock-${phaseKey.replace(":", "-")}-btn`}
          className="ml-auto"
        >
          {busy ? "…" : "🔓 Unlock to re-run"}
        </Button>
      </div>
    );
  }

  return (
    <Button
      size="sm"
      variant="default"
      onClick={lock}
      disabled={busy}
      data-testid={`lock-${phaseKey.replace(":", "-")}-btn`}
      className="bg-emerald-600 hover:bg-emerald-700 text-white"
    >
      {busy ? "…" : "✓ Approve & Lock this phase"}
    </Button>
  );
}


export function Stat({ label, value, danger }) {
  return (
    <div className="border rounded-md p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={`text-lg font-semibold mt-1 ${
          danger ? "text-destructive" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}

export function Section({ title, children }) {
  return (
    <div>
      <div className="text-sm font-medium mb-2">{title}</div>
      {children}
    </div>
  );
}

const DEFAULT_PROPERTIES = [
  { name: "Hidden Monkey Hostel, Chet Singh Ghat", is_partner_property: false },
  { name: "Hidden Monkey Homes, Batasia Loop", is_partner_property: false },
  { name: "Hidden Monkey Hostel, Railway Station", is_partner_property: false },
];

function NewSessionForm({ onCreated }) {
  // Standard 4 OTAs (with dedicated DB columns) + 4 commonly-seen customs.
  // User can add more rows via "+ Add OTA".
  const STD_OTAS = ["GoMMT", "Agoda", "BookingCom", "Hostelworld"];
  const SEED_OTAS = [
    { key: "GoMMT", label: "MakeMyTrip" },
    { key: "Agoda", label: "Agoda" },
    { key: "BookingCom", label: "Booking.com" },
    { key: "Hostelworld", label: "Hostelworld" },
    { key: "Cleartrip", label: "Cleartrip" },
    { key: "Airbnb", label: "Airbnb" },
    { key: "Expedia", label: "Expedia" },
  ];
  
  const [properties, setProperties] = useState(DEFAULT_PROPERTIES);
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [isSavingProperty, setIsSavingProperty] = useState(false);
  const [newPropForm, setNewPropForm] = useState({
    name: "",
    stayflexi_property_id: "",
    is_partner_property: false,
    ignore_offline: false,
    partner_commission_online_pct: "12",
    partner_commission_offline_pct: "12",
  });

  const [form, setForm] = useState({
    property_name: "Hidden Monkey Hostel, Chet Singh Ghat",
    audit_month: "",
    sbe_fee_pct: "2",
    gha_fee_pct: "7",
    is_partner_property: false,
    ignore_offline: false,
    partner_commission_online_pct: "12",
    partner_commission_offline_pct: "12",
    notes: "",
  });

  const loadProperties = async () => {
    try {
      const res = await axios.get(`${API}/properties`);
      if (res.data && Array.isArray(res.data) && res.data.length > 0) {
        setProperties(res.data);
      } else {
        setProperties(DEFAULT_PROPERTIES);
      }
    } catch (e) {
      console.error("Failed to load properties", e);
      setProperties(DEFAULT_PROPERTIES);
    }
  };

  useEffect(() => {
    loadProperties();
  }, []);

  const handlePropertyChange = (val) => {
    const matched = properties.find((p) => p.name === val);
    if (matched) {
      setForm((prev) => ({
        ...prev,
        property_name: val,
        is_partner_property: !!matched.is_partner_property,
        ignore_offline: !!matched.ignore_offline,
        partner_commission_online_pct: String(matched.partner_commission_online_pct ?? "12"),
        partner_commission_offline_pct: String(matched.partner_commission_offline_pct ?? "12"),
      }));
    } else {
      setForm((prev) => ({ ...prev, property_name: val }));
    }
  };

  const handleAddProperty = async () => {
    const trimmedName = newPropForm.name.trim();
    if (!trimmedName) return toast.error("Please enter a property name");
    
    setIsSavingProperty(true);
    try {
      const payload = {
        ...newPropForm,
        name: trimmedName,
        partner_commission_online_pct: Number(newPropForm.partner_commission_online_pct || 12),
        partner_commission_offline_pct: Number(newPropForm.partner_commission_offline_pct || 12),
        stayflexi_property_id: newPropForm.stayflexi_property_id?.trim() || null,
      };
      
      const res = await axios.post(`${API}/properties`, payload);
      const savedProperty = res.data?.property || payload;
      
      toast.success(`Property "${trimmedName}" saved successfully!`);
      setShowAddProperty(false);
      setNewPropForm({
        name: "", stayflexi_property_id: "", is_partner_property: false, 
        ignore_offline: false, partner_commission_online_pct: "12", partner_commission_offline_pct: "12",
      });
      
      await loadProperties();
      setForm((prev) => ({
        ...prev,
        property_name: savedProperty.name,
        is_partner_property: !!savedProperty.is_partner_property,
        ignore_offline: !!savedProperty.ignore_offline,
        partner_commission_online_pct: String(savedProperty.partner_commission_online_pct ?? "12"),
        partner_commission_offline_pct: String(savedProperty.partner_commission_offline_pct ?? "12"),
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Failed to save property");
    } finally {
      setIsSavingProperty(false);
    }
  };

  // Commission % per OTA, keyed by OTA name. Empty string means "not set".
  const [otaRates, setOtaRates] = useState(() => {
    const o = {};
    SEED_OTAS.forEach((s) => {
      if (s.key === "Expedia") o[s.key] = "25";
      else if (s.key === "Hostelworld") o[s.key] = "19";
      else if (["Cleartrip", "Airbnb"].includes(s.key)) o[s.key] = "18";
      else o[s.key] = "";
    });
    return o;
  });
  const [otaLabels, setOtaLabels] = useState(() => {
    const o = {};
    SEED_OTAS.forEach((s) => (o[s.key] = s.label));
    return o;
  });
  const [newOtaName, setNewOtaName] = useState("");
  const [busy, setBusy] = useState(false);

  const addCustomOta = () => {
    const name = newOtaName.trim();
    if (!name) return;
    // Title case-ish key: "Hostelworld" / "MyOta" — keep user's input as-is.
    if (Object.prototype.hasOwnProperty.call(otaRates, name)) {
      toast.message(`${name} is already in the list`);
      setNewOtaName("");
      return;
    }
    setOtaRates({ ...otaRates, [name]: "" });
    setOtaLabels({ ...otaLabels, [name]: name });
    setNewOtaName("");
  };

  const submit = async () => {
    if (!form.audit_month) {
      toast.error("Audit month required (YYYY-MM)");
      return;
    }
    setBusy(true);
    try {
      // Split rates into legacy 3 fields (GoMMT/Agoda/BookingCom) +
      // commissions_custom dict (everything else).
      const num = (v) => (v === "" || v == null ? null : Number(v));
      const customs = {};
      Object.entries(otaRates).forEach(([k, v]) => {
        if (STD_OTAS.includes(k)) return;
        if (v === "" || v == null) return;
        customs[k] = Number(v);
      });
      const payload = {
        property_name: form.property_name,
        audit_month: form.audit_month,
        commission_gommt_pct: num(otaRates.GoMMT),
        commission_agoda_pct: num(otaRates.Agoda),
        commission_bookingcom_pct: num(otaRates.BookingCom),
        commissions_custom: customs,
        sbe_fee_pct: form.sbe_fee_pct === "" ? 2.0 : Number(form.sbe_fee_pct),
        gha_fee_pct: form.gha_fee_pct === "" ? 7.0 : Number(form.gha_fee_pct),
        is_partner_property: form.is_partner_property,
        ignore_offline: form.ignore_offline,
        partner_commission_online_pct: Number(form.partner_commission_online_pct || "12"),
        partner_commission_offline_pct: Number(form.partner_commission_offline_pct || "12"),
        notes: form.notes || null,
      };
      const res = await axios.post(`${API}/audit/session`, payload);
      toast.success("Session created");
      onCreated(res.data);
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const otaKeys = Object.keys(otaRates);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Phase 0 — Initialize</CardTitle>
        <CardDescription>
          One property per session. Set commission % for every channel
          you use up-front — they flow into Phase 3 outlier detection
          and Phase 4 net-revenue calculation.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs">Property</Label>
            <Dialog open={showAddProperty} onOpenChange={setShowAddProperty}>
              <DialogTrigger asChild>
                <Button variant="link" size="sm" className="h-auto p-0 text-xs">+ Add Property</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Add New Property</DialogTitle>
                  <DialogDescription>Create a new property for auditing.</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label className="text-xs">Property Name</Label>
                    <Input value={newPropForm.name} onChange={e => setNewPropForm({...newPropForm, name: e.target.value})} placeholder="Hidden Monkey..." />
                  </div>
                  <div className="grid gap-2">
                    <Label className="text-xs">Stayflexi Property ID (Optional)</Label>
                    <Input value={newPropForm.stayflexi_property_id} onChange={e => setNewPropForm({...newPropForm, stayflexi_property_id: e.target.value})} placeholder="e.g. 30395" />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch checked={newPropForm.is_partner_property} onCheckedChange={c => setNewPropForm({...newPropForm, is_partner_property: c})} />
                    <Label className="text-xs font-medium">This is a Partner Property</Label>
                  </div>
                  
                  {newPropForm.is_partner_property && (
                    <div className="p-3 border rounded-md space-y-3 bg-violet-50/50 dark:bg-violet-900/10">
                      <div className="text-xs font-semibold text-violet-800 dark:text-violet-300">Partner Settings</div>
                      <div className="flex items-center space-x-2">
                        <Switch checked={newPropForm.ignore_offline} onCheckedChange={c => setNewPropForm({...newPropForm, ignore_offline: c})} />
                        <Label className="text-xs">Ignore offline bookings (HMS gets 0% from walk-ins/calls)</Label>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Online HMS Share %</Label>
                          <Input type="number" step="0.1" value={newPropForm.partner_commission_online_pct} onChange={e => setNewPropForm({...newPropForm, partner_commission_online_pct: e.target.value})} />
                        </div>
                        <div>
                          <Label className="text-xs">Offline HMS Share %</Label>
                          <Input type="number" step="0.1" disabled={newPropForm.ignore_offline} value={newPropForm.partner_commission_offline_pct} onChange={e => setNewPropForm({...newPropForm, partner_commission_offline_pct: e.target.value})} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter className="flex justify-end gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowAddProperty(false)}
                    disabled={isSavingProperty}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="button" 
                    onClick={handleAddProperty}
                    disabled={isSavingProperty || !newPropForm.name.trim()}
                  >
                    {isSavingProperty ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save Property"
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Select
            value={form.property_name}
            onValueChange={handlePropertyChange}
          >
            <SelectTrigger data-testid="property-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {properties.map(p => (
                <SelectItem key={p.name} value={p.name}>
                  {p.name}
                  {p.is_partner_property && <Badge variant="outline" className="ml-2 text-[10px] text-violet-600 border-violet-200">Partner</Badge>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Partner Property Checkbox & Configuration */}
        <div className={`p-3.5 rounded-lg border transition-all ${
          form.is_partner_property 
            ? "bg-violet-50/80 border-violet-300 shadow-sm dark:bg-violet-950/20 dark:border-violet-800" 
            : "bg-muted/20 border-border"
        }`}>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Switch
                  id="partner-property-switch"
                  checked={form.is_partner_property}
                  onCheckedChange={(c) => setForm({ ...form, is_partner_property: c })}
                />
                <Label htmlFor="partner-property-switch" className="text-sm font-semibold cursor-pointer">
                  Partner Property
                </Label>
                {form.is_partner_property ? (
                  <Badge className="bg-violet-600 hover:bg-violet-700 text-white text-[10px]">
                    Partner Audit Active
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] text-muted-foreground">
                    Regular Property
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Enable for external properties to compute HMS revenue share and generate settlement statements (Phase 6).
              </p>
            </div>
          </div>

          {form.is_partner_property && (
            <div className="mt-3 pt-3 border-t border-violet-200 dark:border-violet-800/60 space-y-3">
              <div className="flex items-center space-x-2">
                <Switch
                  id="ignore-offline-switch"
                  checked={form.ignore_offline}
                  onCheckedChange={(c) => setForm({ ...form, ignore_offline: c })}
                />
                <Label htmlFor="ignore-offline-switch" className="text-xs font-medium cursor-pointer">
                  Ignore offline bookings (HMS commission is 0% on Walk-in and Direct Phone calls)
                </Label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <div>
                  <Label className="text-xs font-medium text-violet-900 dark:text-violet-200">
                    Online HMS Commission %
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="12"
                    value={form.partner_commission_online_pct}
                    onChange={(e) => setForm({ ...form, partner_commission_online_pct: e.target.value })}
                    className="h-8 mt-1 bg-white dark:bg-zinc-900 border-violet-200"
                  />
                  <span className="text-[10px] text-muted-foreground">Commission on OTAs & Online portals</span>
                </div>
                <div>
                  <Label className="text-xs font-medium text-violet-900 dark:text-violet-200">
                    Offline HMS Commission %
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="12"
                    disabled={form.ignore_offline}
                    value={form.partner_commission_offline_pct}
                    onChange={(e) => setForm({ ...form, partner_commission_offline_pct: e.target.value })}
                    className="h-8 mt-1 bg-white dark:bg-zinc-900 border-violet-200 disabled:opacity-50"
                  />
                  <span className="text-[10px] text-muted-foreground">
                    {form.ignore_offline ? "Disabled (Offline ignored)" : "Commission on Direct / Walk-ins"}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div>
          <Label className="text-xs">Audit month (YYYY-MM)</Label>
          <Input
            data-testid="audit-month-input"
            placeholder="2025-09"
            value={form.audit_month}
            onChange={(e) => setForm({ ...form, audit_month: e.target.value })}
          />
        </div>

        {/* Tech-partner fees */}
        <div className="rounded-md border bg-muted/30 p-3">
          <div className="text-xs font-medium mb-2">
            Tech-partner fees (deducted from gross to get net payout)
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Booking Engine (SBE) %</Label>
              <Input
                data-testid="sbe-fee-input"
                type="number"
                step="0.1"
                placeholder="2"
                value={form.sbe_fee_pct}
                onChange={(e) => setForm({ ...form, sbe_fee_pct: e.target.value })}
              />
              <span className="text-[10px] text-muted-foreground">Direct website engine</span>
            </div>
            <div>
              <Label className="text-xs">Google Hotel Ad (GHA) %</Label>
              <Input
                data-testid="gha-fee-input"
                type="number"
                step="0.1"
                placeholder="7"
                value={form.gha_fee_pct}
                onChange={(e) => setForm({ ...form, gha_fee_pct: e.target.value })}
              />
              <span className="text-[10px] text-muted-foreground">Google campaign traffic</span>
            </div>
          </div>
        </div>

        {/* OTA commission table */}
        <div>
          <div className="text-xs font-medium mb-2">
            OTA commissions (% deducted by the channel)
          </div>
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Channel</TableHead>
                  <TableHead className="text-xs w-32">Commission %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {otaKeys.map((k) => (
                  <TableRow key={k} data-testid={`p0-ota-row-${k}`}>
                    <TableCell className="text-xs font-medium">
                      <span className={k === "Hostelworld" ? "text-amber-600 font-bold" : ""}>
                        {otaLabels[k] || k} {k === "Hostelworld" ? "(No file required)" : ""}
                      </span>
                    </TableCell>
                    <TableCell>
                      {["GoMMT", "Agoda", "BookingCom"].includes(k) ? (
                        <Input
                          data-testid={`p0-comm-${k}-input`}
                          type="text"
                          className="h-8 text-xs text-muted-foreground"
                          value="Based on Actuals"
                          disabled
                        />
                      ) : (
                        <Input
                          data-testid={`p0-comm-${k}-input`}
                          type="number"
                          step="0.1"
                          placeholder="—"
                          className="h-8 text-xs"
                          value={otaRates[k]}
                          onChange={(e) =>
                            setOtaRates({ ...otaRates, [k]: e.target.value })
                          }
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex gap-2 mt-2">
            <Input
              data-testid="p0-add-ota-input"
              placeholder="Add another OTA (e.g. Trivago, Yatra, EaseMyTrip)"
              value={newOtaName}
              onChange={(e) => setNewOtaName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCustomOta();
                }
              }}
              className="text-xs"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="p0-add-ota-btn"
              onClick={addCustomOta}
            >
              + Add
            </Button>
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            Leave the % blank if you don't book through that channel — it
            simply won't appear in Phase 3 / 4. You can also edit these
            anytime later from the session's Commissions Editor.
          </div>
        </div>

        <div>
          <Label className="text-xs">Notes / manual adjustments</Label>
          <Input
            data-testid="notes-input"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>
        <Button data-testid="create-session-btn" onClick={submit} disabled={busy}>
          {busy ? "Creating..." : "Create session"}
        </Button>
      </CardContent>
    </Card>
  );
}

function CustomOtaUpload({ session, refresh }) {
  const [name, setName] = useState("");
  // Strip ota: prefix and any sub-slot tag (e.g. ota:GoMMT:confirmed -> GoMMT)
  const customOtas = Array.from(
    new Set(
      Object.keys(session.files || {})
        .filter((k) => k.startsWith("ota:"))
        .map((k) => k.split(":", 3)[1])
    )
  ).filter((n) => !["GoMMT", "Agoda", "BookingCom", "Hostelworld"].includes(n));

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Custom OTA upload</CardTitle>
        <CardDescription className="text-xs">
          Hostelworld, Cleartrip, Airbnb, Expedia, etc.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input
            data-testid="custom-ota-name-input"
            placeholder="OTA name (e.g. HOSTELWORLD)"
            value={name}
            onChange={(e) => setName(e.target.value.toUpperCase())}
          />
        </div>
        {name && (
          <FileUpload
            session={session}
            role={`ota:${name}`}
            label={`Upload ${name}`}
            onUploaded={refresh}
          />
        )}
        {customOtas.length > 0 && (
          <div className="text-xs">
            <div className="font-medium mb-1">Already uploaded:</div>
            <div className="flex flex-wrap gap-1">
              {customOtas.map((n) => (
                <Badge key={n} variant="secondary">
                  {n}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// eslint-disable-next-line no-unused-vars
function SessionConfigEditor({ session, refresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    audit_month: session.audit_month || "",
    commission_gommt_pct: session.commission_gommt_pct ?? "",
    commission_agoda_pct: session.commission_agoda_pct ?? "",
    commission_bookingcom_pct: session.commission_bookingcom_pct ?? "",
    notes: session.notes ?? "",
  });

  const save = async () => {
    try {
      const payload = {
        audit_month: form.audit_month,
        commission_gommt_pct: form.commission_gommt_pct === "" ? null : Number(form.commission_gommt_pct),
        commission_agoda_pct: form.commission_agoda_pct === "" ? null : Number(form.commission_agoda_pct),
        commission_bookingcom_pct: form.commission_bookingcom_pct === "" ? null : Number(form.commission_bookingcom_pct),
        notes: form.notes,
      };
      await axios.patch(`${API}/audit/session/${session.id}`, payload);
      toast.success("Session config updated");
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    }
  };

  if (!open)
    return (
      <Button
        variant="outline"
        size="sm"
        data-testid="edit-config-btn"
        onClick={() => setOpen(true)}
      >
        Edit config
      </Button>
    );

  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Audit month</Label>
            <Input
              value={form.audit_month}
              onChange={(e) => setForm({ ...form, audit_month: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">GoMMT %</Label>
            <Input
              type="number"
              value={form.commission_gommt_pct}
              onChange={(e) => setForm({ ...form, commission_gommt_pct: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Agoda %</Label>
            <Input
              type="number"
              value={form.commission_agoda_pct}
              onChange={(e) => setForm({ ...form, commission_agoda_pct: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Booking.com %</Label>
            <Input
              type="number"
              value={form.commission_bookingcom_pct}
              onChange={(e) => setForm({ ...form, commission_bookingcom_pct: e.target.value })}
            />
          </div>
        </div>
        <div>
          <Label className="text-xs">Notes</Label>
          <Input
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={save} data-testid="save-config-btn">
            Save
          </Button>
          <Button size="sm" variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CommissionTrendsPanel({ session }) {
  const phase3 = session.phase_results?.phase3 || {};
  const otas = Object.keys(phase3);
  const declared = {
    GoMMT: session.commission_gommt_pct,
    Agoda: session.commission_agoda_pct,
    BookingCom: session.commission_bookingcom_pct,
  };
  if (otas.length === 0)
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Commission Trends</CardTitle>
          <CardDescription className="text-xs">
            Run Phase 3 for at least one OTA to see commission analytics.
          </CardDescription>
        </CardHeader>
      </Card>
    );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Commission Trends — by OTA</CardTitle>
        <CardDescription className="text-xs">
          Median commission inferred from each booking. Bookings outside
          ±3pp band of the OTA's median are flagged as commission outliers.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>OTA</TableHead>
              <TableHead>Declared %</TableHead>
              <TableHead>Inferred Median %</TableHead>
              <TableHead>Within ±0.5pp</TableHead>
              <TableHead>Bookings</TableHead>
              <TableHead>Outliers</TableHead>
              <TableHead>Money at Risk (₹)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {otas.map((ota) => {
              const r = phase3[ota];
              if (!r?.ok) return null;
              const cs = r.commission_summary || {};
              const outliers = Array.isArray(r.commission_outliers) ? r.commission_outliers : [];
              const sf_missing = Array.isArray(r.sf_missing_in_ota) ? r.sf_missing_in_ota : [];
              const moneyAtRisk =
                outliers.reduce((s, o) => s + (o?.potential_leakage || 0), 0) +
                sf_missing.reduce(
                  (s, o) => s + Math.max(o?.sf_net || 0, 0),
                  0
                );
              return (
                <TableRow key={ota} data-testid={`comm-row-${ota}`}>
                  <TableCell className="font-medium">{ota}</TableCell>
                  <TableCell>
                    {declared[ota] != null ? `${declared[ota]}%` : "—"}
                  </TableCell>
                  <TableCell>
                    {cs.median_pct != null ? `${cs.median_pct}%` : "—"}
                  </TableCell>
                  <TableCell>
                    {cs.within_0_5_band_pct_of_bookings ??
                      cs["within_0.5_band_pct_of_bookings"] ??
                      "—"}
                    %
                  </TableCell>
                  <TableCell>{r.matched_count + r.real_discrepancy_count}</TableCell>
                  <TableCell
                    className={
                      r.commission_outlier_count > 0 ? "text-destructive" : ""
                    }
                  >
                    {r.commission_outlier_count}
                  </TableCell>
                  <TableCell
                    className={
                      moneyAtRisk > 0 ? "text-destructive font-medium" : ""
                    }
                  >
                    ₹{Math.round(moneyAtRisk).toLocaleString()}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function ControlBoard({ session, refresh }) {
  // GoMMT specifically supports two sub-slots (e.g. confirmed + cancelled
  // are typically downloaded as separate CSVs from the MMT/Goibibo extranet).
  // Other OTAs use a single slot. Backend concatenates sub-slot files when
  // Phase 3 runs.
  const fileSlots = [
    { role: "master", label: "Stayflexi Master" },
    { role: "tax", label: "Tax Report" },
    { role: "payment_gateway", label: "Stayflexi Gateway Report" },
    { role: "razorpay_raw", label: "Razorpay Raw Dashboard" },
    { role: "ota:GoMMT:confirmed", label: "GoMMT — Confirmed / Active" },
    { role: "ota:GoMMT:cancelled", label: "GoMMT — Cancelled" },
    { role: "ota:Agoda", label: "Agoda" },
    { role: "ota:BookingCom", label: "Booking.com" },
    { role: "ota:Hostelworld", label: "Hostelworld" },
  ];
  // Backward-compat: if a legacy single-slot ota:GoMMT file is on the
  // session (older sessions), surface it as an extra row.
  if (session.files?.["ota:GoMMT"]) {
    fileSlots.splice(2, 0, {
      role: "ota:GoMMT",
      label: "GoMMT (legacy single file)",
    });
  }
  // Include any custom OTA files uploaded
  const customOtaRoles = Object.keys(session.files || {}).filter((k) => {
    if (!k.startsWith("ota:")) return false;
    if (k === "ota:GoMMT" || k === "ota:Agoda" || k === "ota:BookingCom" || k === "ota:Hostelworld") return false;
    if (k.startsWith("ota:GoMMT:")) return false;  // already rendered above
    return true;
  });
  for (const r of customOtaRoles) {
    fileSlots.push({ role: r, label: r.slice(4).replace(/:/g, " — ") });
  }

  const hasMaster = !!session.files?.master;
  const hasTax = !!session.files?.tax;
  // Distinct OTA types — collapse `ota:GoMMT`, `ota:GoMMT:confirmed`, and
  // `ota:GoMMT:cancelled` into a single "GoMMT" entry so we render exactly
  // one Phase 3 button per OTA regardless of how many sub-slot files were
  // uploaded.
  const otaTypes = Array.from(
    new Set(
      Object.keys(session.files || {})
        .filter((k) => k.startsWith("ota:"))
        .map((k) => k.split(":", 3)[1])  // ota:GoMMT:confirmed -> GoMMT
    )
  );

  const runPhase = async (num, body = {}) => {
    try {
      const url =
        num === 3
          ? `${API}/audit/session/${session.id}/phase3`
          : `${API}/audit/session/${session.id}/phase${num}`;
      const res = await axios.post(url, num === 3 ? body : undefined);
      if (res.data && res.data.ok === false) throw new Error(res.data.error || `Phase ${num} backend error`);
      toast.success(`Phase ${num} complete`);
      refresh();
    } catch (e) {
      toast.error(`Phase ${num} failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const removeFile = async (role) => {
    if (!window.confirm(`Remove ${role}? Phase results depending on this will be cleared.`)) return;
    try {
      await axios.delete(
        `${API}/audit/session/${session.id}/file/${encodeURIComponent(role)}`
      );
      toast.success(`${role} removed`);
      refresh();
    } catch (e) {
      toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const uploadFile = async (role, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `${API}/audit/session/${session.id}/upload?role=${encodeURIComponent(role)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`${role} uploaded (${res.data.rows} rows)`);
      refresh();
    } catch (e) {
      toast.error(`Upload failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const phaseStatus = {
    1: session.phase_results?.phase1?.ok ? "Complete" : null,
    2: session.phase_results?.phase2?.ok ? "Complete" : null,
    3: Object.keys(session.phase_results?.phase3 || {}).length > 0 ? "Complete" : null,
    4: session.phase_results?.phase4?.ok ? "Complete" : null,
  };

  return (
    <Card data-testid="control-board">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Control Board</CardTitle>
        <CardDescription className="text-xs">
          Upload / replace / remove files, then run any phase from here.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Files row */}
        <div>
          <div className="text-xs font-medium mb-2">Files</div>
          <div className="w-full">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">File</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Filename</TableHead>
                  <TableHead className="text-xs w-64">Upload / Replace</TableHead>
                  <TableHead className="text-xs w-24">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fileSlots.map((slot) => {
                  const uploaded = !!session.files?.[slot.role];
                  return (
                    <TableRow key={slot.role} data-testid={`file-row-${slot.role}`}>
                      <TableCell className="text-xs font-medium">{slot.label}</TableCell>
                      <TableCell>
                        {uploaded ? (
                          <Badge variant="secondary" data-testid={`status-uploaded-${slot.role}`}>
                            Uploaded
                          </Badge>
                        ) : (
                          <Badge variant="outline">Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {session.files?.[slot.role] || "—"}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          data-testid={`cb-upload-${slot.role}`}
                          className="h-8 text-xs"
                          onChange={(e) => uploadFile(slot.role, e.target.files?.[0])}
                        />
                      </TableCell>
                      <TableCell>
                        {uploaded && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`cb-delete-${slot.role}`}
                            onClick={() => removeFile(slot.role)}
                          >
                            Remove
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Phase run buttons */}
        <div>
          <div className="text-xs font-medium mb-2">Run Phases</div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              data-testid="cb-run-phase1"
              disabled={!hasMaster}
              onClick={() => runPhase(1)}
              variant={phaseStatus[1] ? "outline" : "default"}
            >
              {phaseStatus[1] ? "↻ Phase 1" : "Run Phase 1"}
              {phaseStatus[1] && (
                <Badge variant="secondary" className="ml-2">
                  ✓
                </Badge>
              )}
            </Button>
            <Button
              size="sm"
              data-testid="cb-run-phase2"
              disabled={!hasMaster || !hasTax}
              onClick={() => runPhase(2)}
              variant={phaseStatus[2] ? "outline" : "default"}
            >
              {phaseStatus[2] ? "↻ Phase 2" : "Run Phase 2"}
              {phaseStatus[2] && (
                <Badge variant="secondary" className="ml-2">
                  ✓
                </Badge>
              )}
            </Button>
            {otaTypes.map((ota) => {
              const done = !!session.phase_results?.phase3?.[ota];
              return (
                <Button
                  key={ota}
                  size="sm"
                  data-testid={`cb-run-phase3-${ota}`}
                  disabled={!hasMaster}
                  onClick={() =>
                    runPhase(3, {
                      ota_type: ota,
                      threshold_pct: 1.0,
                      commission_outlier_pct: 3.0,
                      source_filter: ["GoMMT", "Agoda", "BookingCom", "Hostelworld"].includes(ota)
                        ? null
                        : [ota],
                    })
                  }
                  variant={done ? "outline" : "default"}
                >
                  {done ? `↻ Phase 3 · ${ota}` : `Run Phase 3 · ${ota}`}
                  {done && (
                    <Badge variant="secondary" className="ml-2">
                      ✓
                    </Badge>
                  )}
                </Button>
              );
            })}
            <Button
              size="sm"
              data-testid="cb-run-phase4"
              disabled={!hasMaster}
              onClick={() => runPhase(4)}
              variant={phaseStatus[4] ? "outline" : "default"}
            >
              {phaseStatus[4] ? "↻ Phase 4" : "Run Phase 4"}
              {phaseStatus[4] && (
                <Badge variant="secondary" className="ml-2">✓</Badge>
              )}
            </Button>
            <Button
              size="sm"
              onClick={() => runPhase(5)}
              variant={phaseStatus[5] ? "outline" : "default"}
            >
              {phaseStatus[5] ? "↻ Phase 5" : "Run Phase 5"}
              {phaseStatus[5] && (
                <Badge variant="secondary" className="ml-2">✓</Badge>
              )}
            </Button>
            {session.is_partner_property && (
              <Button
                size="sm"
                onClick={() => runPhase(6)}
                disabled={!phaseStatus[5]}
                variant={phaseStatus[6] ? "outline" : "default"}
                className={!phaseStatus[6] ? "bg-violet-600 hover:bg-violet-700" : "border-violet-600 text-violet-600"}
              >
                {phaseStatus[6] ? "↻ Phase 6 (Partner)" : "Run Phase 6"}
                {phaseStatus[6] && (
                  <Badge variant="secondary" className="ml-2">✓</Badge>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CommissionsEditor({ session, refresh }) {
  // Build the full list of OTAs: standard 3 + uploaded custom + discovered in master
  const [busy, setBusy] = useState(false);
  const [addName, setAddName] = useState("");
  const [rates, setRates] = useState(() => ({
    GoMMT: session.commission_gommt_pct ?? "",
    Agoda: session.commission_agoda_pct ?? "",
    BookingCom: session.commission_bookingcom_pct ?? "",
    ...(session.commissions_custom || {}),
  }));

  useEffect(() => {
    setRates({
      GoMMT: session.commission_gommt_pct ?? "",
      Agoda: session.commission_agoda_pct ?? "",
      BookingCom: session.commission_bookingcom_pct ?? "",
      ...(session.commissions_custom || {}),
    });
  }, [session]);

  const customOtasFromFiles = Array.from(
    new Set(
      Object.keys(session.files || {})
        .filter((k) => k.startsWith("ota:"))
        .map((k) => k.split(":", 3)[1])  // ota:GoMMT:confirmed -> GoMMT
    )
  ).filter((n) => !["GoMMT", "Agoda", "BookingCom", "Hostelworld"].includes(n));

  // Pre-populate known custom names in the table
  const allOtas = Array.from(
    new Set([
      "GoMMT",
      "Agoda",
      "BookingCom",
      ...customOtasFromFiles,
      ...Object.keys(session.commissions_custom || {}),
    ])
  );

  const save = async () => {
    setBusy(true);
    try {
      const STD = new Set(["GoMMT", "Agoda", "BookingCom", "Hostelworld"]);
      const custom = {};
      Object.entries(rates).forEach(([k, v]) => {
        if (STD.has(k)) return;
        if (v === "" || v === null || v === undefined) return;
        custom[k] = Number(v);
      });
      const payload = {
        commission_gommt_pct: rates.GoMMT === "" ? null : Number(rates.GoMMT),
        commission_agoda_pct: rates.Agoda === "" ? null : Number(rates.Agoda),
        commission_bookingcom_pct:
          rates.BookingCom === "" ? null : Number(rates.BookingCom),
        commissions_custom: custom,
      };
      await axios.patch(`${API}/audit/session/${session.id}`, payload);
      toast.success("Commissions saved");
      refresh();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const addOta = () => {
    const name = addName.trim().toUpperCase();
    if (!name) return;
    setRates({ ...rates, [name]: "" });
    setAddName("");
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Commission Rates (per OTA)</CardTitle>
        <CardDescription className="text-xs">
          Declared rate per OTA. The engine still infers the median from
          actual data and flags outliers — these values are used as the
          expected baseline in outlier messages.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">OTA</TableHead>
              <TableHead className="text-xs w-32">Declared %</TableHead>
              <TableHead className="text-xs">Inferred median %</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {allOtas.map((ota) => {
              const p3 = session.phase_results?.phase3?.[ota];
              const inferred = p3?.commission_summary?.median_pct;
              return (
                <TableRow key={ota} data-testid={`comm-editor-${ota}`}>
                  <TableCell className="text-xs font-medium">
                    <span className={ota === "Hostelworld" ? "text-amber-600 font-bold" : ""}>
                      {ota} {ota === "Hostelworld" ? "(No file required)" : ""}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Input
                      data-testid={`comm-input-${ota}`}
                      type="number"
                      step="0.01"
                      className="h-8 text-xs"
                      value={rates[ota] ?? ""}
                      onChange={(e) =>
                        setRates({ ...rates, [ota]: e.target.value })
                      }
                    />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {inferred != null ? `${inferred}%` : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <div className="flex gap-2 items-end pt-2">
          <div className="flex-1">
            <Label className="text-xs">Add another OTA (custom)</Label>
            <Input
              data-testid="add-ota-input"
              placeholder="e.g. HOSTELWORLD, CLEARTRIP, AIRBNB, EXPEDIA"
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            data-testid="add-ota-btn"
            onClick={addOta}
          >
            Add
          </Button>
        </div>

        <Button
          data-testid="save-commissions-btn"
          size="sm"
          onClick={save}
          disabled={busy}
        >
          {busy ? "Saving…" : "Save commissions"}
        </Button>
      </CardContent>
    </Card>
  );
}

function SessionView({ session, refresh, reset, openRenameModal }) {
  const currentPhases = { ...PHASE_LABELS };
  if (session?.is_partner_property) {
    currentPhases.phase6 = "Phase 6 — Partner Settlement";
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2">
                <span>{session.property_name}</span>
                {openRenameModal && (
                  <button
                    type="button"
                    title={`Rename "${session.property_name}"`}
                    onClick={() => openRenameModal(session.property_name)}
                    className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors inline-flex items-center justify-center"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
              </CardTitle>
              <CardDescription>
                Audit month: {session.audit_month} · Session ID: {session.id.slice(0, 8)}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Link to="/reports">
                <Button variant="default" size="sm" className="bg-primary hover:bg-primary/90">
                  View Reports
                </Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                data-testid="reset-session-btn"
                onClick={reset}
              >
                Switch session
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      <ControlBoard session={session} refresh={refresh} />

      <CommissionsEditor session={session} refresh={refresh} />

      <CommissionTrendsPanel session={session} />

      <details className="border rounded-md">
        <summary className="cursor-pointer text-sm font-semibold p-3">
          Custom OTA Upload (Hostelworld, Cleartrip, Airbnb, Expedia, etc.)
        </summary>
        <div className="p-3 pt-0">
          <CustomOtaUpload session={session} refresh={refresh} />
        </div>
      </details>

      <Tabs defaultValue="phase1" className="w-full">
        <AuditStepper phases={currentPhases} session={session} />
        <TabsContent value="phase1" className="mt-4">
          <ErrorBoundary>
            <Card>
              <CardHeader>
                <CardTitle>{PHASE_LABELS.phase1}</CardTitle>
              </CardHeader>
              <CardContent>
                <Phase1 session={session} refresh={refresh} />
              </CardContent>
            </Card>
          </ErrorBoundary>
        </TabsContent>
        <TabsContent value="phase2" className="mt-4">
          <ErrorBoundary>
            <Card>
              <CardHeader>
                <CardTitle>{PHASE_LABELS.phase2}</CardTitle>
              </CardHeader>
              <CardContent>
                <Phase2 session={session} refresh={refresh} />
              </CardContent>
            </Card>
          </ErrorBoundary>
        </TabsContent>
        <TabsContent value="phase3" className="mt-4">
          <ErrorBoundary>
            <Card>
              <CardHeader>
                <CardTitle>{PHASE_LABELS.phase3}</CardTitle>
              </CardHeader>
              <CardContent>
                <Phase3 session={session} refresh={refresh} />
              </CardContent>
            </Card>
          </ErrorBoundary>
        </TabsContent>
        <TabsContent value="phase4" className="mt-4">
          <ErrorBoundary>
            <Card>
              <CardHeader>
                <CardTitle>{PHASE_LABELS.phase4}</CardTitle>
              </CardHeader>
              <CardContent>
                <Phase4 session={session} refresh={refresh} />
              </CardContent>
            </Card>
          </ErrorBoundary>
        </TabsContent>
        <TabsContent value="phase5" className="mt-4">
          <ErrorBoundary>
            <Card>
              <CardHeader>
                <CardTitle>{PHASE_LABELS.phase5}</CardTitle>
              </CardHeader>
              <CardContent>
                <Phase5 session={session} refresh={refresh} />
              </CardContent>
            </Card>
          </ErrorBoundary>
        </TabsContent>
        {session.is_partner_property && (
          <TabsContent value="phase6" className="mt-4">
            <ErrorBoundary>
              <Card>
                <CardHeader>
                  <CardTitle>{currentPhases.phase6}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Phase6 session={session} refresh={refresh} />
                </CardContent>
              </Card>
            </ErrorBoundary>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function AppHeader({ onLogout }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const email = localStorage.getItem("user_email");

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };
  return (
    <header className="flex items-center justify-between border-b pb-4">
      <div className="flex items-center gap-3">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              data-testid="hamburger-menu-btn"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80">
            <SheetHeader>
              <SheetTitle>Settlr by HMS</SheetTitle>
              <SheetDescription>Navigate</SheetDescription>
            </SheetHeader>
            <nav className="mt-6 space-y-1">
              <button
                data-testid="nav-home-btn"
                className="w-full text-left px-3 py-2 rounded-md hover:bg-accent text-sm"
                onClick={() => go("/")}
              >
                ↩ Audit workspace
              </button>
              <button
                className="w-full text-left px-3 py-2 rounded-md hover:bg-accent text-sm text-primary font-medium"
                onClick={() => go("/reports")}
              >
                📊 Master
              </button>
              <button
                className="w-full text-left px-3 py-2 rounded-md hover:bg-accent text-sm text-primary font-medium"
                onClick={() => go("/analytics")}
              >
                📈 Analytics
              </button>
              <div className="text-xs text-muted-foreground px-3 pt-3 pb-1">
                Documentation
              </div>
              <button
                data-testid="nav-audit-tree-btn"
                className="w-full text-left px-3 py-2 rounded-md hover:bg-accent text-sm"
                onClick={() => go("/docs/audit-tree")}
              >
                🌳 Audit Tree
              </button>
              <button
                data-testid="nav-design-btn"
                className="w-full text-left px-3 py-2 rounded-md hover:bg-accent text-sm"
                onClick={() => go("/docs/design")}
              >
                🛠 Design Logic
              </button>
              <button
                data-testid="nav-training-btn"
                className="w-full text-left px-3 py-2 rounded-md hover:bg-accent text-sm"
                onClick={() => go("/docs/training")}
              >
                📘 User Training
              </button>
            </nav>
          </SheetContent>
        </Sheet>
        <div className="flex items-center gap-3">
          <img src="/logo.jpg" alt="HMS Logo" className="h-10 w-10 object-cover rounded-xl border border-border shadow-sm" />
          <div>
            <div className="flex items-center gap-2">
              <Link
                to="/"
                className="text-2xl font-bold tracking-tight no-underline text-foreground font-mono"
              >
                Settlr
              </Link>
              <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px] py-0 px-1.5 uppercase font-semibold">
                by HMS
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground">
              Automated revenue audit & settlement engine
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4">
        {email && <div className="text-xs text-muted-foreground hidden md:block">{email}</div>}
        {onLogout && (
          <Button variant="ghost" size="icon" onClick={onLogout} title="Sign Out">
            <LogOut className="h-4 w-4 text-muted-foreground hover:text-foreground" />
          </Button>
        )}
      </div>
    </header>
  );
}

function DocsPage({ slug, title }) {
  const [content, setContent] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setContent("");
    setError(null);
    axios
      .get(`${API}/docs/${slug}`)
      .then((res) => {
        if (active) setContent(res.data.content || "");
      })
      .catch((e) => {
        if (active) setError(e.response?.data?.detail || e.message);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>
            Internal documentation · slug: <code>{slug}</code>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <p className="text-destructive text-sm">{error}</p>
          ) : !content ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <article
              className="prose prose-sm max-w-none"
              data-testid={`doc-content-${slug}`}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  pre: ({ node, ...props }) => (
                    <pre
                      className="bg-muted p-3 rounded text-xs overflow-x-auto"
                      {...props}
                    />
                  ),
                  code: ({ inline, ...props }) =>
                    inline ? (
                      <code className="bg-muted px-1 rounded text-xs" {...props} />
                    ) : (
                      <code className="text-xs" {...props} />
                    ),
                  h1: ({ node, ...props }) => (
                    <h1 className="text-2xl font-bold mt-6 mb-3" {...props} />
                  ),
                  h2: ({ node, ...props }) => (
                    <h2 className="text-xl font-semibold mt-5 mb-2" {...props} />
                  ),
                  h3: ({ node, ...props }) => (
                    <h3 className="text-lg font-semibold mt-4 mb-2" {...props} />
                  ),
                  p: ({ node, ...props }) => (
                    <p className="text-sm mb-3 leading-6" {...props} />
                  ),
                  li: ({ node, ...props }) => (
                    <li className="text-sm" {...props} />
                  ),
                  table: ({ node, ...props }) => (
                    <table
                      className="text-xs border-collapse my-3"
                      {...props}
                    />
                  ),
                  th: ({ node, ...props }) => (
                    <th
                      className="border px-2 py-1 bg-muted text-left"
                      {...props}
                    />
                  ),
                  td: ({ node, ...props }) => (
                    <td className="border px-2 py-1" {...props} />
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            </article>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Workspace() {
  const [session, setSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [expandedProps, setExpandedProps] = useState({});

  // ── Property Renaming State & Handlers ─────────────────────────────────────
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [propertyToRename, setPropertyToRename] = useState("");
  const [newPropertyName, setNewPropertyName] = useState("");
  const [isRenamingProperty, setIsRenamingProperty] = useState(false);

  const openRenameModal = (propName) => {
    setPropertyToRename(propName);
    setNewPropertyName(propName);
    setRenameModalOpen(true);
  };

  const handleRenameProperty = async (e) => {
    if (e) e.preventDefault();
    const trimmed = newPropertyName.trim();
    if (!trimmed) {
      toast.error("Property name cannot be empty");
      return;
    }
    if (trimmed === propertyToRename) {
      setRenameModalOpen(false);
      return;
    }

    setIsRenamingProperty(true);
    try {
      const res = await axios.put(`${API}/properties/${encodeURIComponent(propertyToRename)}/rename`, {
        new_name: trimmed,
      });
      toast.success(`Property renamed to "${trimmed}" across ${res.data?.sessions_updated || 0} session(s)!`);
      setRenameModalOpen(false);

      await loadSessions();

      if (session && session.property_name === propertyToRename) {
        setSession(prev => prev ? { ...prev, property_name: trimmed } : prev);
      }
    } catch (err) {
      console.error("Failed to rename property", err);
      toast.error(err.response?.data?.detail || "Failed to rename property");
    } finally {
      setIsRenamingProperty(false);
    }
  };

  const renderRenameModal = () => (
    <Dialog open={renameModalOpen} onOpenChange={setRenameModalOpen}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Rename Property</DialogTitle>
          <DialogDescription>
            Updating this property name will instantly update all historical audit sessions and future settlement statements without requiring any re-upload or recalculation.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleRenameProperty} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="current-prop-name" className="text-xs text-muted-foreground">Current Property Name</Label>
            <Input id="current-prop-name" value={propertyToRename} disabled className="bg-muted text-muted-foreground" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-prop-name">New Property Name</Label>
            <Input
              id="new-prop-name"
              placeholder="e.g. Nirmala"
              value={newPropertyName}
              onChange={(e) => setNewPropertyName(e.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setRenameModalOpen(false)}
              disabled={isRenamingProperty}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isRenamingProperty || !newPropertyName.trim() || newPropertyName.trim() === propertyToRename}
            >
              {isRenamingProperty ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Renaming...
                </>
              ) : (
                "Save & Update All Sessions"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const loadSessions = async () => {
    try {
      const res = await axios.get(`${API}/audit/sessions?t=${Date.now()}`);
      setSessions(res.data || []);
    } catch (e) {
      console.error(e);
      if (e.response && (e.response.status === 401 || e.response.status === 403)) return;
      toast.error("Failed to load sessions. Check your connection and try again.");
    }
  };

  const refresh = async () => {
    if (!session) return;
    try {
      const res = await axios.get(`${API}/audit/session/${session.id}?t=${Date.now()}`);
      setSession(res.data);
    } catch (e) {
      console.error(e);
      if (e.response && (e.response.status === 401 || e.response.status === 403)) return;
      toast.error("Failed to refresh session data.");
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const renderSessionCard = (s) => (
    <div
      key={s.id}
      className={`flex items-center justify-between border rounded-md p-2 transition-colors ${
        s.verified
          ? "bg-green-50/50 border-green-200 dark:bg-green-900/20 dark:border-green-900/30"
          : "bg-card"
      }`}
      data-testid={`session-${s.id}`}
    >
      <div className="text-sm">
        <div className="font-medium flex items-center gap-2">
          {s.property_name}
          {s.verified && (
            <Badge variant="outline" className="text-[10px] h-4 border-green-500 text-green-600 px-1 bg-white dark:bg-slate-950">Verified</Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          {s.audit_month} · {s.id?.slice(0, 8)}
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          className="text-destructive border-destructive/30 hover:bg-destructive/10"
          data-testid={`delete-session-${s.id}-btn`}
          onClick={async () => {
            if (!window.confirm("Are you sure you want to completely delete this session?")) return;
            try {
              await axios.delete(`${API}/audit/session/${s.id}`);
              toast.success("Session deleted");
              loadSessions();
            } catch (e) {
              toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
            }
          }}
        >
          Delete
        </Button>
        <Button
          size="sm"
          variant="outline"
          data-testid={`open-session-${s.id}-btn`}
          onClick={() => setSession(s)}
        >
          Open
        </Button>
      </div>
    </div>
  );

  if (session) {
    return (
      <>
        <SessionView
          session={session}
          refresh={refresh}
          reset={() => { setSession(null); loadSessions(); }}
          openRenameModal={openRenameModal}
        />
        {renderRenameModal()}
      </>
    );
  }

  const renderPropertyGroups = (sessionsList, groupByYear = false) => {
    const byProperty = {};
    sessionsList.forEach(s => {
      const prop = s.property_name || "Unknown Property";
      if (!byProperty[prop]) byProperty[prop] = [];
      byProperty[prop].push(s);
    });

    return Object.entries(byProperty)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([propName, propSessions]) => {
        if (!groupByYear) {
          return (
            <details key={propName} className="group/prop border rounded-md bg-card overflow-hidden mt-2 first:mt-0">
              <summary className="px-3 py-2 text-sm font-medium bg-muted/20 hover:bg-muted/40 cursor-pointer flex justify-between items-center transition-colors [&::-webkit-details-marker]:hidden">
                <div className="flex items-center gap-2">
                  <span>{propName} ({propSessions.length})</span>
                  <button
                    type="button"
                    title={`Rename "${propName}"`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openRenameModal(propName);
                    }}
                    className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors inline-flex items-center justify-center"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </div>
                <span className="text-muted-foreground transition-transform group-open/prop:rotate-180">▼</span>
              </summary>
              <div className="p-3 space-y-2 border-t">
                {propSessions.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).map(renderSessionCard)}
              </div>
            </details>
          );
        } else {
          const byYear = {};
          propSessions.forEach(s => {
            let year = "Unknown Year";
            if (s.audit_month) {
              const parts = s.audit_month.split("-");
              if (parts.length > 0 && parts[0].length === 4) year = parts[0];
              else year = parts[parts.length - 1]; // fallback
            }
            if (!byYear[year]) byYear[year] = [];
            byYear[year].push(s);
          });

          return (
            <details key={propName} className="group/prop border rounded-md bg-card overflow-hidden mt-2 first:mt-0">
              <summary className="px-3 py-2 text-sm font-medium bg-muted/20 hover:bg-muted/40 cursor-pointer flex justify-between items-center transition-colors [&::-webkit-details-marker]:hidden">
                <div className="flex items-center gap-2">
                  <span>{propName} ({propSessions.length})</span>
                  <button
                    type="button"
                    title={`Rename "${propName}"`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openRenameModal(propName);
                    }}
                    className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors inline-flex items-center justify-center"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </div>
                <span className="text-muted-foreground transition-transform group-open/prop:rotate-180">▼</span>
              </summary>
              <div className="p-3 space-y-2 border-t bg-muted/5">
                {Object.entries(byYear).sort(([a], [b]) => b.localeCompare(a)).map(([year, yearSessions]) => (
                  <details key={year} open className="group/year border rounded-md bg-card overflow-hidden">
                    <summary className="px-3 py-1.5 text-xs font-medium bg-muted/30 hover:bg-muted/50 cursor-pointer flex justify-between items-center transition-colors [&::-webkit-details-marker]:hidden">
                      <span>{year} ({yearSessions.length})</span>
                      <span className="text-muted-foreground transition-transform group-open/year:rotate-180">▼</span>
                    </summary>
                    <div className="p-2 space-y-2 border-t">
                      {yearSessions.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).map(renderSessionCard)}
                    </div>
                  </details>
                ))}
              </div>
            </details>
          );
        }
      });
  };

  // ── Audit Status Tracker ─────────────────────────────────────────────────
  // Derives the full month range from the earliest session to today, then
  // assigns a status for each property+month:
  //   green  = session exists AND verified
  //   yellow = session exists but NOT verified (in progress)
  //   red    = no session found for that month (not started)
  const buildTracker = () => {
    if (!sessions.length) return {};

    // Collect all known months (YYYY-MM) and property names
    const allMonths = new Set();
    const byProp = {};
    sessions.forEach(s => {
      const prop = s.property_name || "Unknown";
      const month = (s.audit_month || "").slice(0, 7); // keep YYYY-MM
      if (!byProp[prop]) byProp[prop] = {};
      if (month) {
        allMonths.add(month);
        // If there are multiple sessions for same month, prefer verified
        const existing = byProp[prop][month];
        if (!existing || s.verified) {
          byProp[prop][month] = s.verified ? "green" : "yellow";
        }
      }
    });

    // Build a sorted list from earliest known month → current month
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const earliest = [...allMonths].sort()[0];
    const monthRange = [];
    if (earliest) {
      let [y, m] = earliest.split("-").map(Number);
      const [ey, em] = currentMonth.split("-").map(Number);
      while (y < ey || (y === ey && m <= em)) {
        monthRange.push(`${y}-${String(m).padStart(2, "0")}`);
        m++;
        if (m > 12) { m = 1; y++; }
      }
    }

    return { byProp, monthRange };
  };

  const { byProp: trackerData, monthRange } = buildTracker();
  const toggleProp = (prop) => setExpandedProps(p => ({ ...p, [prop]: !p[prop] }));

  const STATUS_COLORS = {
    green:  { dot: "#22c55e", bg: "bg-green-500",  label: "Verified" },
    yellow: { dot: "#eab308", bg: "bg-yellow-400", label: "In Progress" },
    red:    { dot: "#ef4444", bg: "bg-red-500",    label: "Not Started" },
  };

  const fmtMonth = (ym) => {
    if (!ym) return ym;
    const [y, m] = ym.split("-");
    return new Date(Number(y), Number(m) - 1, 1)
      .toLocaleString("default", { month: "short", year: "2-digit" });
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <NewSessionForm
        onCreated={(s) => {
          setSession(s);
          loadSessions();
        }}
      />
      <Card>
        <CardHeader>
          <CardTitle>Existing sessions</CardTitle>
        </CardHeader>
        <CardContent>
          {sessions.length === 0 && (
            <p className="text-sm text-muted-foreground">No sessions yet.</p>
          )}
          <div className="space-y-4">
            {sessions.some(s => !s.verified) && (
              <details open className="group border rounded-md bg-card overflow-hidden">
                <summary className="px-3 py-2 text-sm font-medium bg-muted/30 hover:bg-muted/50 cursor-pointer flex justify-between items-center transition-colors [&::-webkit-details-marker]:hidden">
                  <span>In Progress ({sessions.filter(s => !s.verified).length})</span>
                  <span className="text-muted-foreground transition-transform group-open:rotate-180">▼</span>
                </summary>
                <div className="p-3 border-t bg-muted/10">
                  {renderPropertyGroups([...sessions].filter(s => !s.verified), false)}
                </div>
              </details>
            )}

            {sessions.some(s => s.verified) && (
              <details className="group border rounded-md bg-card overflow-hidden">
                <summary className="px-3 py-2 text-sm font-medium bg-muted/30 hover:bg-muted/50 cursor-pointer flex justify-between items-center transition-colors [&::-webkit-details-marker]:hidden">
                  <span>Completed and Verified ({sessions.filter(s => s.verified).length})</span>
                  <span className="text-muted-foreground transition-transform group-open:rotate-180">▼</span>
                </summary>
                <div className="p-3 border-t bg-muted/10">
                  {renderPropertyGroups([...sessions].filter(s => s.verified), true)}
                </div>
              </details>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Audit Status Tracker ── */}
      <Card className="h-fit">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <span>Audit Tracker</span>
            <span className="text-xs font-normal text-muted-foreground">
              {monthRange?.length ? `${fmtMonth(monthRange[0])} – ${fmtMonth(monthRange[monthRange.length - 1])}` : ""}
            </span>
          </CardTitle>
          {/* Legend */}
          <div className="flex gap-3 pt-1">
            {Object.entries(STATUS_COLORS).map(([k, v]) => (
              <span key={k} className="flex items-center gap-1 text-[11px] text-muted-foreground">
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${v.bg}`} />
                {v.label}
              </span>
            ))}
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          {!trackerData || Object.keys(trackerData).length === 0 ? (
            <p className="text-sm text-muted-foreground">No sessions yet.</p>
          ) : (
            <div className="space-y-1">
              {Object.keys(trackerData).sort().map(prop => {
                const isOpen = expandedProps[prop] ?? false;
                const propMonths = trackerData[prop];
                const counts = { green: 0, yellow: 0, red: 0 };
                (monthRange || []).forEach(m => {
                  const s = propMonths[m] || "red";
                  counts[s]++;
                });

                return (
                  <div key={prop} className="border rounded-md overflow-hidden">
                    {/* Property header row */}
                    <div className="w-full flex items-center justify-between px-3 py-2 bg-muted/20 hover:bg-muted/40 transition-colors">
                      <div className="flex items-center gap-1.5 truncate flex-1 mr-2">
                        <button
                          onClick={() => toggleProp(prop)}
                          className="text-sm font-medium truncate text-left hover:underline"
                          title={prop}
                        >
                          {prop}
                        </button>
                        <button
                          type="button"
                          title={`Rename "${prop}"`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openRenameModal(prop);
                          }}
                          className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors inline-flex items-center justify-center"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <button
                        onClick={() => toggleProp(prop)}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        {/* Mini summary counts */}
                        <div className="flex items-center gap-1.5">
                          {counts.green > 0 && (
                            <span className="flex items-center gap-0.5 text-[10px] text-green-600 font-semibold">
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                              {counts.green}
                            </span>
                          )}
                          {counts.yellow > 0 && (
                            <span className="flex items-center gap-0.5 text-[10px] text-yellow-600 font-semibold">
                              <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 inline-block" />
                              {counts.yellow}
                            </span>
                          )}
                          {counts.red > 0 && (
                            <span className="flex items-center gap-0.5 text-[10px] text-red-500 font-semibold">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
                              {counts.red}
                            </span>
                          )}
                        </div>
                        <span className="text-muted-foreground text-xs" style={{ display: "inline-block", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }}>▼</span>
                      </button>
                    </div>

                    {/* Month dot grid */}
                    {isOpen && (
                      <div className="px-3 py-2.5 border-t bg-muted/5">
                        <div className="flex flex-wrap gap-1.5">
                          {(monthRange || []).map(month => {
                            const status = propMonths[month] || "red";
                            const color = STATUS_COLORS[status];
                            const sess = sessions.find(
                              s => (s.property_name || "Unknown") === prop
                                && (s.audit_month || "").startsWith(month)
                            );
                            return (
                              <button
                                key={month}
                                title={`${fmtMonth(month)} — ${color.label}${sess ? " · Click to open" : ""}`}
                                onClick={() => sess && setSession(sess)}
                                className={`flex flex-col items-center gap-0.5 px-1.5 py-1 rounded text-[10px] font-medium border transition-all
                                  ${
                                    status === "green"  ? "border-green-300 bg-green-50 text-green-700 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400" :
                                    status === "yellow" ? "border-yellow-300 bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-400" :
                                                          "border-red-200 bg-red-50 text-red-500 dark:bg-red-900/20 dark:border-red-900 dark:text-red-400"
                                  }
                                  ${sess ? "hover:scale-105 hover:shadow-sm cursor-pointer" : "cursor-default opacity-60"}
                                `}
                              >
                                <span className={`w-2 h-2 rounded-full ${color.bg}`} />
                                {fmtMonth(month)}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      {renderRenameModal()}
    </div>
  );
}

function ReportsDashboard() {
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    axios.get(`${API}/audit/sessions?t=${Date.now()}`).then(res => {
      setSessions(res.data || []);
      setBusy(false);
    }).catch(e => {
      toast.error("Failed to load history");
      setBusy(false);
    });
  }, []);

  if (busy) return <p className="text-sm p-8">Loading Master reports...</p>;

  const grouped = {};
  (sessions || []).forEach(s => {
    if (!grouped[s.property_name]) grouped[s.property_name] = [];
    grouped[s.property_name].push(s);
  });

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Master Dashboard</h1>
      <p className="text-muted-foreground text-sm">View past finalized audit reports across all properties.</p>
      {Object.keys(grouped).length === 0 ? (
        <div className="p-8 border rounded-md bg-muted/20 text-center text-muted-foreground">No historical data available.</div>
      ) : (
        <div className="grid gap-6">
          {Object.keys(grouped).sort().map(prop => (
            <Card key={prop}>
              <CardHeader>
                <CardTitle>{prop}</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Audit Month</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Outflow</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {grouped[prop].sort((a,b) => String(b.audit_month || "").localeCompare(String(a.audit_month || ""))).map(s => (
                      <TableRow key={s.id}>
                        <TableCell className="font-medium">{s.audit_month}</TableCell>
                        <TableCell>
                          {s.verified ? <Badge className="bg-green-600 hover:bg-green-600/80">Verified</Badge> : <Badge variant="secondary">Draft</Badge>}
                        </TableCell>
                        <TableCell>
                          <OutflowManager session={s} onSaved={() => {
                            axios.get(`${API}/audit/sessions?t=${Date.now()}`).then(res => setSessions(res.data || []));
                          }} />
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button variant="outline" size="sm" asChild>
                            <Link to={`/?session=${s.id}`}>View Audit</Link>
                          </Button>
                          <Button variant="outline" size="sm" disabled={!s.verified} onClick={async () => {
                            try {
                              const token = localStorage.getItem("google_token");
                              const res = await fetch(`${API}/audit/session/${s.id}/report.pdf`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              if (!res.ok) throw new Error(`Server returned ${res.status}: Failed to download PDF`);
                              let filename = `Audit_Report_${s.audit_month}.pdf`;
                              const cd = res.headers.get("Content-Disposition");
                              if (cd) {
                                const m = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
                                if (m) filename = m[1];
                              }
                              const blob = await res.blob();
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = filename;
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              document.body.removeChild(a);
                            } catch (e) {
                              alert("Authentication failed or file unavailable.");
                            }
                          }}>
                            PDF
                          </Button>
                          <Button variant="outline" size="sm" disabled={!s.verified} onClick={async () => {
                            try {
                              const token = localStorage.getItem("google_token");
                              const res = await fetch(`${API}/audit/session/${s.id}/report.xlsx`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              if (!res.ok) throw new Error(`Server returned ${res.status}: Failed to download Excel`);
                              let filename = `Audit_Data_${s.audit_month}.xlsx`;
                              const cd = res.headers.get("Content-Disposition");
                              if (cd) {
                                const m = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
                                if (m) filename = m[1];
                              }
                              const blob = await res.blob();
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = filename;
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              document.body.removeChild(a);
                            } catch (e) {
                              alert("Authentication failed or file unavailable.");
                            }
                          }}>
                            Excel
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        const userInfo = await res.json();
        localStorage.setItem("google_token", tokenResponse.access_token);
        localStorage.setItem("user_email", userInfo.email);
        onLogin();
      } catch (err) {
        toast.error('Failed to fetch user info');
      }
    },
    onError: () => {
      toast.error('Google Login Failed');
    },
    prompt: 'select_account'
  });

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-between relative overflow-hidden font-sans selection:bg-violet-500 selection:text-white">
      {/* Background ambient glow & grid mesh */}
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-tr from-violet-600/25 via-indigo-500/20 to-purple-500/10 blur-[120px] pointer-events-none rounded-full" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#33415515_1px,transparent_1px),linear-gradient(to_bottom,#33415515_1px,transparent_1px)] bg-[size:3.5rem_3.5rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_10%,#000_70%,transparent_100%)] pointer-events-none" />

      {/* Top Header */}
      <header className="relative z-10 w-full max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.jpg" alt="HMS Logo" className="h-9 w-9 object-cover rounded-xl border border-slate-800 shadow-sm" />
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold tracking-tight text-white font-mono">Settlr</span>
            <span className="text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300 border border-violet-500/30">
              by HMS
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span>v2.4 Financial Engine</span>
        </div>
      </header>

      {/* Main Content Card */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-slate-900/90 backdrop-blur-xl border border-slate-800 shadow-2xl rounded-2xl p-8 relative overflow-hidden">
            {/* Top card accent line */}
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-violet-500 to-transparent" />

            <div className="text-center space-y-3">
              <div className="inline-flex p-3 rounded-2xl bg-slate-800/80 border border-slate-700/60 shadow-inner mb-1">
                <img src="/logo.jpg" alt="Settlr by HMS" className="h-12 w-12 object-cover rounded-xl" />
              </div>
              
              <div className="space-y-1">
                <div className="flex items-center justify-center gap-2">
                  <h1 className="text-3xl font-extrabold tracking-tight text-white font-mono">Settlr</h1>
                  <span className="text-[11px] tracking-widest uppercase font-bold px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300 border border-violet-500/30">
                    by HMS
                  </span>
                </div>
                <p className="text-sm font-medium text-slate-300">
                  Automated Revenue Audit & Settlement Engine
                </p>
                <p className="text-xs text-slate-400 leading-relaxed pt-1">
                  Reconcile Stayflexi PMS, OTA statements, and payment gateways with zero leakage.
                </p>
              </div>

              {/* Feature pillars */}
              <div className="grid grid-cols-3 gap-2 py-4 my-2 border-y border-slate-800/80 text-left">
                <div className="p-2.5 rounded-xl bg-slate-800/40 border border-slate-800/60">
                  <div className="text-violet-400 font-mono text-xs font-bold">100%</div>
                  <div className="text-[11px] text-slate-400 mt-0.5 font-medium leading-tight">Automated Recon</div>
                </div>
                <div className="p-2.5 rounded-xl bg-slate-800/40 border border-slate-800/60">
                  <div className="text-emerald-400 font-mono text-xs font-bold">Zero</div>
                  <div className="text-[11px] text-slate-400 mt-0.5 font-medium leading-tight">Leakage Alert</div>
                </div>
                <div className="p-2.5 rounded-xl bg-slate-800/40 border border-slate-800/60">
                  <div className="text-indigo-400 font-mono text-xs font-bold">Phase 1-6</div>
                  <div className="text-[11px] text-slate-400 mt-0.5 font-medium leading-tight">Audit Pipeline</div>
                </div>
              </div>

              {/* Action Button */}
              <div className="pt-2 space-y-3">
                <Button
                  onClick={() => login()}
                  className="w-full flex items-center justify-center gap-3 bg-white hover:bg-slate-100 text-slate-950 font-semibold py-3 px-4 rounded-xl shadow-lg shadow-violet-500/10 hover:shadow-violet-500/20 transition-all duration-200 border-0 text-sm h-12"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span>Sign in with Google</span>
                </Button>

                <p className="text-[11px] text-slate-500 leading-tight">
                  Restricted to authorized Hidden Monkey Stays finance accounts.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 w-full max-w-6xl mx-auto px-6 py-6 border-t border-slate-900/80 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <span>Part of the HMS Suite:</span>
          <span className="text-slate-400 font-medium">Hey Tarrif</span>
          <span>·</span>
          <span className="text-slate-400 font-medium">Kritiq</span>
          <span>·</span>
          <span className="text-violet-400 font-semibold font-mono">Settlr</span>
        </div>
        <div>© 2026 Hidden Monkey Stays · Financial Operations</div>
      </footer>
    </div>
  );
}

function App() {
  // Try to load initial state from localStorage for persistence
  const initialView = localStorage.getItem("google_token") ? "APP" : "LOGIN";
  const [appView, setAppView] = useState(initialView);

  const handleLogout = () => {
    googleLogout();
    localStorage.removeItem("google_token");
    localStorage.removeItem("user_email");
    setAppView("LOGIN");
  };

  if (appView === "LOGIN") {
    return (
      <>
        <Toaster richColors position="top-right" />
        <LoginScreen onLogin={() => setAppView("APP")} />
      </>
    );
  }

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-background text-foreground">
        <Toaster richColors position="top-right" />
        <div className="w-full mx-auto px-4 lg:px-8 py-8 space-y-6" style={{ maxWidth: "1600px" }}>
          <AppHeader onLogout={handleLogout} />
          <Routes>
            <Route path="/" element={<Workspace />} />
            <Route path="/reports" element={<ReportsDashboard />} />
            <Route path="/analytics" element={<AnalyticsDashboard />} />
            <Route
              path="/docs/audit-tree"
              element={<DocsPage slug="audit-tree" title="Audit Tree" />}
            />
            <Route
              path="/docs/design"
              element={<DocsPage slug="design" title="Design Logic" />}
            />
            <Route
              path="/docs/training"
              element={<DocsPage slug="training" title="User Training" />}
            />
          </Routes>
        </div>
      </div>
    </BrowserRouter>
  );
}

export default App;
